import React, { useMemo, useCallback, useState } from 'react';

import { Card, Stack, ProgressBar, Row, Col } from '../../../ui-kit';
import { IconCircle, IconArrowRight } from '../../../ui-kit/icons';
import moment from 'moment';
import chroma from 'chroma-js';
import { formatTimeHumanRelative } from '../../../../utils/time';
import colors from '../../../../utils/colors';
import { themeType } from '../../../ui-kit/helpers';
import { splitIntervalsByDay } from '../../../../utils/schedules';
import AvailabilityRequirementStatus from '../../../general/AvailabilityRequirementStatus';

const getDayPercentage = time => {
  time = moment(time);
  const totalMinutesPassed = time.hours() * 60 + time.minutes();
  const totalMinutesInDay = 24 * 60;
  // Calculate the percentage of the day that has passed
  return Math.min((totalMinutesPassed / totalMinutesInDay) * 100, 100);
};

const IntervalCard = ({
  requirement,
  interval,
  className,
  expandByDefault,
  handleContextMenu,
  showNamesWhenOrangeOrRed
}) => {
  const understaffed = interval.warning_level === 'below_required';

  const [expanded, setExpanded] = useState(expandByDefault);

  const expandCard = useCallback(() => setExpanded(!expanded), [expanded]);

  const intervals = useMemo(() => splitIntervalsByDay([interval]) || [], [interval]);

  // create a data structure to be consumed by the timeline renderer.
  // It returns an array of objects that contain information about how to display each
  // timeline: the timestamp, and how to color it.
  const timelines = useMemo(
    () =>
      intervals.map(i => {
        const startTimePg = getDayPercentage(i.start_time);
        let actualEndPg = getDayPercentage(i.end_time);
        // Handle the case where the interval ends at midnight of the next day
        // This happens if the end time is different from the start time and the
        // end time percentage returns 0%
        if (interval.start_time !== i.end_time && actualEndPg === 0) actualEndPg = 100;

        const endTimePg = startTimePg > actualEndPg ? 100 : actualEndPg;

        // Length is the percentage of a day that the interval represents, from
        // midnight to midnight
        const length = endTimePg - startTimePg;
        const remainder = 100 - endTimePg;

        return { startTimePg, length, remainder };
      }),
    [intervals]
  );

  const color = colors[interval.warning_level] || 'black';
  const bgColor = chroma(color).alpha(0.2).css();

  const PeriodContainer = expanded ? Card.Header : Card.Body;

  const handleContextMenuLocal = useCallback(
    event => {
      handleContextMenu(event, {
        startTime: interval.start_time,
        endTime: interval.end_time,
        groupIds: requirement.group_ids || []
      });
    },
    [handleContextMenu, interval, requirement.group_ids]
  );

  const isNow = moment().isBetween(interval.start_time, interval.end_time);
  const startTimeDisplay = isNow ? (
    <span className="text-uppercase">{I18n.t('now')}</span>
  ) : (
    formatTimeHumanRelative(interval.start_time)
  );

  return (
    <Card
      className={className}
      style={{ borderWidth: '2px', borderColor: color, overflow: 'hidden' }}
      onContextMenu={handleContextMenuLocal}
    >
      <PeriodContainer className="pt-1 pb-2" onClick={expandCard}>
        <Stack direction="horizontal" gap={3}>
          <IconCircle style={{ color: color }} size="lg" />
          <Stack gap={1}>
            <Row>
              <Col xs={5}>{startTimeDisplay}</Col>
              <Col xs={2} className="text-center">
                <IconArrowRight style={{ color: themeType === 'light' ? 'var(--bs-dark)' : 'var(--bs-light)' }} />
              </Col>
              <Col xs={5} className="text-end">
                {formatTimeHumanRelative(interval.end_time)}
              </Col>
            </Row>
            {timelines.map((day, index) => (
              <Stack key={index} direction="horizontal" gap={3}>
                <ProgressBar style={{ width: '100%', height: '4px' }}>
                  <ProgressBar
                    variant={themeType === 'light' ? 'light' : 'secondary'}
                    now={day.startTimePg}
                    max={100}
                  />
                  <ProgressBar variant={understaffed ? 'danger' : 'info'} now={day.length} max={100} />
                  <ProgressBar variant={themeType === 'light' ? 'light' : 'secondary'} now={day.remainder} max={100} />
                </ProgressBar>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </PeriodContainer>
      {expanded && (
        <Card.Body className="p-2" style={{ background: bgColor }}>
          <AvailabilityRequirementStatus interval={interval} showNamesWhenOrangeOrRed={showNamesWhenOrangeOrRed} />
        </Card.Body>
      )}
    </Card>
  );
};

export default IntervalCard;
